import { createSelector } from 'reselect';
import { DriverDto, TicketDto } from '@trucktrax/trucktrax-ts-common';
import { mapFilterInitialState, PlantFilterOption } from '../reducers/mapFilterReducers';
import { ReduxState } from '..';

// DEV-3913: a ticket's truck type is determined solely by its assigned driver.
// Returns undefined when the driver can't be resolved (no driver on the ticket,
// or driver data hasn't loaded yet) so callers can choose not to hide the ticket.
// NOTE: kept local (not in util/ticketUtil.ts) to avoid a circular import —
// ticketUtil.ts transitively imports back through ticketsService/authService/Main
// into incomingMaterialsSelectors.ts, which imports from this file.
export function resolveTicketIsExternal(
  ticket: TicketDto,
  allDrivers: DriverDto[] = [],
): boolean | undefined {
  const driver = allDrivers.find(d => d.url === ticket.driver?.url);
  return driver?.isExternal;
}

// DEV-3913: mirrors the Map Filters Internal/External Trucks toggles, but ONLY for
// this ticket list. The map markers hide everything when both toggles are off; here
// we deliberately treat "both off" the same as "both on" (show every ticket) so the
// list never appears to go empty just because both switches happen to be disabled.
// A ticket whose driver can't be resolved is shown regardless, for the same reason.
export function shouldShowTicketForTruckTypeFilter(
  isExternal: boolean | undefined,
  showInternal: boolean,
  showExternal: boolean,
): boolean {
  if (isExternal === undefined || (!showInternal && !showExternal)) {
    return true;
  }
  return isExternal ? showExternal : showInternal;
}

const plantList = (state: Partial<ReduxState>) => state.mapFilters?.plantList ?? [];

export const selectedPlantsSelector = createSelector(
  [plantList],
  (plantList: PlantFilterOption[]) => {
    const selectedPlants = plantList.filter(isSelected);
    return [...selectedPlants].sort((a, b) => a.id - b.id);
  },
);

function isSelected(plant: PlantFilterOption): boolean {
  return plant.isSelected && !!plant.url;
}

export const selectedPlantUrlsSelector = createSelector(
  [selectedPlantsSelector],
  (plantList: PlantFilterOption[]) => plantList.map(p => p.url ?? '').filter(Boolean),
);

const driversWithActiveTicketsMatchingSelectedPlants = (
  state: Partial<ReduxState>,
  selectedPlantUrls: string[],
): DriverDto[] => {
  const tickets = state.ticketsList
    ?.filter(ticket => selectedPlantUrls.includes(ticket.plant?.url ?? ''))
    .filter(Boolean);
  const driversOnTickets = state.allDrivers?.filter(driver =>
    tickets?.some(ticket => ticket.driver?.url === driver.url),
  );
  return driversOnTickets ?? [];
};

const ticketsMatchingMapFilters = (
  state: Partial<ReduxState>,
  selectedPlantUrls: string[],
): TicketDto[] => {
  const ticketSource = state.ticketsList ?? [];
  const {
    showTrucks: showInternal = mapFilterInitialState.showTrucks,
    showExtTrucks: showExternal = mapFilterInitialState.showExtTrucks,
  } = state.mapFilters ?? {};
  const allDrivers = state.allDrivers ?? [];
  const tickets = ticketSource
    .filter(ticket => selectedPlantUrls.includes(ticket.plant?.url ?? ''))
    .filter(ticket =>
      shouldShowTicketForTruckTypeFilter(
        resolveTicketIsExternal(ticket, allDrivers),
        showInternal,
        showExternal,
      ),
    )
    .filter(Boolean);
  return tickets;
};

export const ticketsMatchingMapFiltersSelector = createSelector(
  [ticketsMatchingMapFilters],
  (tickets: TicketDto[]) => tickets,
);

export const driversWithActiveTicketsMatchingSelectedPlantsSelector = createSelector(
  [driversWithActiveTicketsMatchingSelectedPlants],
  (drivers: DriverDto[]) => drivers ?? [],
);

export function filterDriversWithActiveTicketsMatchingProductlineAndPlants(
  tickets: TicketDto[] = [],
  drivers: DriverDto[] = [],
  selectedPlantUrls: string[] = [],
) {
  const ticketList = tickets
    .filter(ticket => selectedPlantUrls.includes(ticket.plant?.url ?? ''))
    .filter(Boolean);
  const driversOnTickets = drivers.filter(driver =>
    ticketList?.some(ticket => ticket.driver?.url === driver.url),
  );
  return driversOnTickets;
}
